import { useEffect, useMemo, useRef, useState } from 'react';
import { useApolloClient, useQuery } from '@apollo/client';
import { isEqual } from 'lodash';

import removeTypename from 'commons/removeTypename';
import { NUMBER_OF_PRODUCTS_TO_SHOW } from 'commons/constants';
import useIsRefreshCache from 'hooks/useIsRefreshCache';
import addDerivedProductData from './helpers/addDerivedProductData';

// This hook provides a reusable way to load all products for a product listing
// page, such as the category page. It first loads just initial product data
// (including filters), then when the browser is idle it loads all products.

// Uses useQuery (not useSuspenseQuery) so the query runs client-side with auth.
// This ensures trade users see correct prices on category pages (SSR would run
// without auth and return regular prices).

// To enable this functionality, it requires that the specified GraphQL query
// supports a limit variable which allows -1 to be passed in to load all.

const useProductListingQuery = (query, options = {}) => {
  const {
    variables,
    limit = NUMBER_OF_PRODUCTS_TO_SHOW,
    skip: skipQuery = false,
    serverSidePaginationEnabled = false,
    serverData,
    ...otherOptions
  } = options;

  const hasLoadingAllDataStartedRef = useRef(false);
  const prevVariables = useRef({ ...variables });
  const [allData, setAllData] = useState(null);
  const isRefreshCache = useIsRefreshCache();
  const isAllDataInInitialRequest = limit === -1;

  // Get the name of the query (e.g. getCategoryPage) from the query object to
  // support different queries that return the same data structure
  const queryName =
    query?.definitions?.[0]?.selectionSet?.selections?.[0]?.alias?.value ??
    query?.definitions?.[0]?.name?.value;

  // Skip the query if the query name is not available or if skip is set to
  // true in options object
  const skip = skipQuery || !queryName;
  const skipAllDataLoading = skip || serverSidePaginationEnabled;

  const { data: initialData, loading } = useQuery(query, {
    fetchPolicy: 'cache-and-network',
    variables: {
      ...variables,
      limit,
      refreshCache: isRefreshCache,
    },
    skip,
    ...otherOptions,
  });

  // Use server-preloaded data while the client-side query is still loading.
  // This allows the initial render to show actual content (product images)
  // instead of a loading placeholder, which drastically improves LCP.
  // Once the client query resolves with real user-location-aware data
  // (correct pricing, availability), it takes over automatically.
  const effectiveInitialData = initialData ?? serverData;
  const isInitialDataLoading = loading && !effectiveInitialData;
  const client = useApolloClient();

  useEffect(() => {
    if (isAllDataInInitialRequest || skipAllDataLoading) {
      return;
    }

    // If loading of all products has not yet started, load all products when
    // the browser is idle
    if (
      !hasLoadingAllDataStartedRef.current ||
      !isEqual(prevVariables.current, variables)
    ) {
      hasLoadingAllDataStartedRef.current = true;
      prevVariables.current = { ...variables };

      window.requestIdleCallback(async () => {
        const { data: updatedAllData } = await client.query({
          query,
          variables: {
            ...variables,
            limit: -1,
            refreshCache: isRefreshCache,
          },
          skip: skipAllDataLoading,
          ...otherOptions,
        });

        setAllData(updatedAllData);
      });
    }
  }, [
    client,
    isAllDataInInitialRequest,
    isRefreshCache,
    otherOptions,
    query,
    skipAllDataLoading,
    variables,
  ]);

  const isAllDataLoading =
    !serverSidePaginationEnabled && !isAllDataInInitialRequest && !allData;
  const dataResponse = allData ?? effectiveInitialData;
  const data = dataResponse?.[queryName];

  const pageData = useMemo(() => (data ? removeTypename(data) : {}), [data]);

  // Add any data which can be derived from the response, which is not included
  // in the initial network response for efficiency
  const { products: productsWithoutDerivedData, upholsteryOptions } = pageData;

  const products = useMemo(
    () =>
      productsWithoutDerivedData?.map(product =>
        addDerivedProductData({ product, upholsteryOptions })
      ) ?? [],
    [productsWithoutDerivedData, upholsteryOptions]
  );

  return {
    ...pageData,
    isAllDataLoading,
    isInitialDataLoading,
    products,
  };
};

export default useProductListingQuery;
